// @ts-check
/// <reference path="au-cart-types.js" />

/**
 * @class
 */
class auCartApiClient {
  static bufferUrl = 'https://cch-apigateway.azurewebsites.net';
  static tenantId = '1148';

  /**
   * Updates the quantity of a specific line item in the cart.
   *
   * @param {string} itemKey - The unique key (ID) of the line item to update.
   * @param {number} value - The new quantity for the line item.
   * @returns {Promise<ShopifyCart|undefined>} A promise that resolves with the updated cart object, or `undefined` if an error occurred during the request.
   * @throws {Error} If the network request fails (though the error is logged and not rethrown).
   */
  static async changeLineItemQuantity(itemKey, value) {
    try {
      const url = `/cart/change.js`;
      const body = {
        id: itemKey,
        quantity: value,
      };
      return await fetch(url, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then((res) => res.json());
    } catch (error) {
      console.error('Error while change line item quantity', error);
    }
  }

  /**
   * Updates the quantity of a specific line item in the cart.
   *
   * @param {string} itemKey - The unique key (ID) of the line item to update.
   * @param {number} value - The new quantity for the line item.
   * @returns {Promise<ShopifyCart|undefined>} A promise that resolves with the updated cart object, or `undefined` if an error occurred during the request.
   * @throws {Error} If the network request fails (though the error is logged and not rethrown).
   */
  static async updateLineItemQuantity(itemKey, value) {
    try {
      const url = `/cart/update.js`;
      const updates = {
        [itemKey]: value,
      };
      return await fetch(url, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      }).then((res) => res.json());
    } catch (error) {
      console.error('Error while update line item quantity', error);
    }
  }

  /**
   * Updates the quantity of a specific line item in the cart.
   *
   * @param {Array<string>} itemKeys - The array with unique key (ID) of the line item to update.
   * @param {number} value - The new quantity for the line item.
   * @returns {Promise<ShopifyCart|undefined>} A promise that resolves with the updated cart object, or `undefined` if an error occurred during the request.
   * @throws {Error} If the network request fails (though the error is logged and not rethrown).
   */
  static async updateLineItemsQuantity(itemKeys, value) {
    try {
      const url = `/cart/update.js`;
      const updates = /** @type {Record<string, number>} */ ({});
      
      itemKeys.forEach((itemKey) => {
        updates[itemKey] = value;
      });

      return await fetch(url, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      }).then((res) => res.json());
    } catch (error) {
      console.error('Error while update line item quantity', error);
    }
  }

  /**
   * Adds a product to the cart via Shopify's `/cart/add.js` endpoint.
   *
   * @param {number} productId - The ID of the product variant to add to the cart.
   * @param {number} quantity - The number of units to add.
   * @returns {Promise<ShopifyCart|undefined>} A promise that resolves with the response data from the server.
   * @throws {Error} If the network request fails (though the error is logged and not rethrown).
   */
  static async addToCart(productId, quantity) {
    try {
      const url = '/cart/add.js';
      const payload = JSON.stringify({
        id: productId,
        quantity,
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      });

      return await response.json();
    } catch (error) {
      console.error('Error while adding product to cart', error);
    }
  }

  /**
   * Removes a line item from the cart by setting its quantity to zero.
   *
   * @param {string} itemKey - The unique key (ID) of the line item to remove.
   * @returns {Promise<boolean>} A promise that resolves with the updated cart object, or `undefined` if an error occurred during the request.
   * @throws {Error} If the network request fails (though the error is logged and not rethrown).
   */
  static async removeLineItem(itemKey) {
    try {
      const url = `/cart/change?id=${itemKey}&quantity=0`;
      await fetch(url);
      return true;
    } catch (error) {
      console.error('Error while remove line item', error);
      return false;
    }
  }

  /**
   * Deletes the white label attribute from the cart by setting it to `null`.
   *
   * @returns {Promise<ShopifyCart|undefined>} A promise that resolves with the updated cart object, or `undefined` if an error occurred during the request.
   * @throws {Error} If the network request fails (though the error is logged and not rethrown).
   */
  static async deleteWhiteLabelCartAttribute() {
    try {
      const url = `/cart/update.js`;
      return await fetch(url, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { '__customers-canvas-white-label': null },
        }),
      }).then((res) => res.json());
    } catch (error) {
      console.log('Error while deleting white label cart attribute', error);
    }
  }

  /**
   * Sets white label attribute in the cart.
   *
   * @returns {Promise<ShopifyCart|undefined>} A promise that resolves with the updated cart object, or `undefined` if an error occurred during the request.
   * @throws {Error} If the network request fails (though the error is logged and not rethrown).
   */
  static async setWhiteLabelCartAttribute() {
    try {
      const url = '/cart/update.js';
      return await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { '__customers-canvas-white-label': true },
        }),
      }).then((res) => res.json());
    } catch (error) {
      console.log('Error while setting white label cart attribute', error);
    }
  }

  /**
   * Fetches the current cart data from Shopify.
   *
   * @returns {Promise<ShopifyCart|undefined>} A promise that resolves with the cart object, or `undefined` if an error occurred during the request.
   * @throws {Error} If the network request fails (though the error is logged and not rethrown).
   */
  static async getCart() {
    try {
      const url = `/cart.js`;
      return await fetch(url).then((res) => res.json());
    } catch (error) {
      console.log('Error while getting cart', error);
    }
  }

  /**
   * Fetches the raw project data for a given cart item from the buffer service.
   *
   * @param {ShopifyCartItem} cartItem - The cart item containing the `_projectId`.
   * @returns {Promise<RawProject|undefined>} A promise that resolves with the raw project data or undefined if failed.
   */
  static async getRawProject(cartItem) {
    try {
      const projectId = cartItem.properties?._projectId;
      if (!projectId) {
        throw new Error('Cart item does not have a _projectId');
      }
      const url = new URL(auCartApiClient.bufferUrl);
      url.pathname = `/api/buffer/v1/tenants/${auCartApiClient.tenantId}/projects/${projectId}`;
      return await fetch(url.href).then((res) => res.json());
    } catch (error) {
      console.error('Error while fetching raw project', error);
    }
  }
}
